import { useQuery, keepPreviousData, type UseQueryOptions } from '@tanstack/react-query';
import { api, type ListQuery, type PaginatedResponse } from '@/lib/api-client';

export interface ListQueryOptions {
  /**
   * Re-ask the server for this list every N ms. Omitted — the default for every
   * caller that does not ask — means no polling at all, exactly as before.
   *
   * Only the finance queues pass one: they are driven by a WebSocket, and a
   * screen whose only refresh mechanism is a socket has no way to notice that
   * the socket died (#292).
   */
  refetchInterval?: number | undefined;
}

/**
 * The options one list query is built from. Exported so the polling contract is
 * assertable without mounting React Query.
 */
export function listQueryOptions<T>(
  resource: string,
  params: ListQuery = {},
  options: ListQueryOptions = {},
): UseQueryOptions<PaginatedResponse<T>> {
  return {
    queryKey: [resource, params],
    queryFn: () => api.listPaginated<T>(resource, params),
    placeholderData: keepPreviousData,
    enabled: resource !== '',
    ...(options.refetchInterval
      ? {
          refetchInterval: options.refetchInterval,
          // A hidden tab stops polling. An operator with the backoffice parked
          // in a background tab all day would otherwise keep every list warm
          // for a screen nobody is looking at; the socket, and the refetch the
          // tab does when it becomes visible again, cover that case for free.
          refetchIntervalInBackground: false,
        }
      : {}),
  };
}

/**
 * Thin wrapper around `useQuery` for listing a REST resource.
 *
 *   const { data, isLoading } = useListQuery<Member>('/api/bo/members', {
 *     page, pageSize, search,
 *   });
 *
 * The cache key is `[resource, params]`, so `invalidateQueries({ queryKey: [resource] })`
 * refreshes every page/filter variant.
 */
export function useListQuery<T>(
  resource: string,
  params: ListQuery = {},
  options: ListQueryOptions = {},
) {
  return useQuery<PaginatedResponse<T>>(listQueryOptions<T>(resource, params, options));
}
